/**
 * Field-level AES-256-GCM for GPS coordinates in Firestore.
 * Must stay byte-compatible with `pear_mobile/lib/utils/location_crypto.dart`.
 *
 * Format v1: `{ v: 1, iv: b64, c: b64, kid?: 'loc-v1' }` — plaintext `"lat,lng"` at 6 d.p.
 * Legacy `{ lat, lng }` still opens on read.
 *
 * Production keys come from Cloud Function `issueLocationDek` (not baked into
 * the bundle). D0b Part 1: we hold **two** DEKs — current (write) + previous
 * (read) — so v1 home pins still open after a rotation. Do not set
 * `LOCATION_MASTER_KEY` until this build is on field devices.
 *
 * Dev/test still derive the v1 pepper locally so Jest and `npm start` work
 * before Functions are deployed — but only when no DEK has been installed.
 */

const VERSION = 1;
export const KID_V1 = 'loc-v1';
export const KID_V2 = 'loc-v2';

/** Browser Web Crypto (Jest polyfills `globalThis.crypto` in the unit test). */
function webCrypto(): Crypto {
  const c = globalThis.crypto;
  if (!c?.subtle) {
    throw new Error('Web Crypto API unavailable');
  }
  return c;
}

function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  bytes.forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function sha256Utf8(text: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(text);
  const digest = await webCrypto().subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

async function importAesKey(raw: Uint8Array): Promise<CryptoKey> {
  return webCrypto().subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

let currentKey: CryptoKey | null = null;
let currentKid = KID_V1;
let previousKey: CryptoKey | null = null;
let previousKid = KID_V1;
const keysByKid = new Map<string, CryptoKey>();
let dekInFlight: Promise<void> | null = null;
let installedForOrg = '';

function rememberKey(kid: string, key: CryptoKey): void {
  keysByKid.set(kid, key);
}

/** Install the 32-byte write DEK (standard base64) from `issueLocationDek`. */
export async function installDekB64(
  dekB64: string,
  opts?: {
    kid?: string;
    previousDekB64?: string;
    previousKid?: string;
    older?: { dekB64: string; kid?: string }[];
  },
): Promise<void> {
  currentKey = await importAesKey(base64ToBytes(dekB64));
  currentKid = opts?.kid && opts.kid.length > 0 ? opts.kid : KID_V1;
  keysByKid.clear();
  rememberKey(currentKid, currentKey);
  previousKey = null;
  previousKid = KID_V1;
  if (opts?.previousDekB64 && opts.previousDekB64.length > 0 && opts.previousDekB64 !== dekB64) {
    previousKey = await importAesKey(base64ToBytes(opts.previousDekB64));
    previousKid = opts.previousKid && opts.previousKid.length > 0 ? opts.previousKid : KID_V1;
    rememberKey(previousKid, previousKey);
  }
  for (const item of opts?.older ?? []) {
    if (!item.dekB64 || item.dekB64 === dekB64 || item.dekB64 === opts?.previousDekB64) continue;
    const key = await importAesKey(base64ToBytes(item.dekB64));
    const kid = item.kid && item.kid.length > 0 ? item.kid : KID_V1;
    rememberKey(kid, key);
  }
}

export async function installFromCallablePayload(raw: {
  dek?: string;
  kid?: string;
  previous?: { dek?: string; kid?: string };
  older?: { dek?: string; kid?: string }[];
}): Promise<void> {
  if (typeof raw.dek !== 'string' || raw.dek.length === 0) return;
  const older = Array.isArray(raw.older)
    ? raw.older
        .filter((item) => typeof item?.dek === 'string' && item.dek.length > 0)
        .map((item) => ({ dekB64: item.dek as string, kid: item.kid }))
    : undefined;
  await installDekB64(raw.dek, {
    kid: raw.kid,
    previousDekB64: raw.previous?.dek,
    previousKid: raw.previous?.kid,
    older,
  });
}

export function clearLocationDek(): void {
  currentKey = null;
  currentKid = KID_V1;
  previousKey = null;
  previousKid = KID_V1;
  keysByKid.clear();
  installedForOrg = '';
}

async function devFallbackKey(): Promise<CryptoKey | null> {
  if (process.env.NODE_ENV === 'production') return null;
  const pepper = 'trasse-location-v1/pear-bc51f/field-encryption';
  return importAesKey(await sha256Utf8(pepper));
}

/**
 * Fetch the DEK from Cloud Functions when signed in. Coalesces in-flight
 * calls. A school asks for that school's key; no school keeps today's shared
 * key. Dev/test fall back to the local v1 pepper if the Function is down.
 */
export async function ensureLocationDek(orgCode?: string | null): Promise<boolean> {
  const org = String(orgCode || '').trim();
  if (currentKey && (!org || installedForOrg === org)) return true;
  if (dekInFlight) {
    await dekInFlight;
    if (currentKey && (!org || installedForOrg === org)) return true;
  }
  dekInFlight = (async () => {
    try {
      const { auth, functions, httpsCallable } = await import('../firebase');
      if (!auth.currentUser) return;
      if (org && installedForOrg !== org) clearLocationDek();
      const fn = httpsCallable<
        { orgCode?: string },
        { dek?: string; kid?: string; previous?: { dek?: string; kid?: string }; older?: { dek?: string; kid?: string }[] }
      >(
        functions,
        'issueLocationDek',
        { timeout: 15000 },
      );
      const res = await fn(org ? { orgCode: org } : {});
      if (res.data) {
        await installFromCallablePayload(res.data);
        installedForOrg = org;
      }
    } catch {
      /* Function not deployed yet — dev fallback below. */
    }
  })();
  try {
    await dekInFlight;
  } finally {
    dekInFlight = null;
  }
  if (!currentKey) {
    const fb = await devFallbackKey();
    if (fb) {
      currentKey = fb;
      currentKid = KID_V1;
      rememberKey(KID_V1, fb);
      installedForOrg = '';
    }
  }
  return currentKey != null;
}

async function getWriteKey(): Promise<CryptoKey> {
  if (currentKey) return currentKey;
  if (typeof window !== 'undefined') {
    await ensureLocationDek();
    if (currentKey) return currentKey;
  }
  const fb = await devFallbackKey();
  if (fb) {
    currentKey = fb;
    currentKid = KID_V1;
    rememberKey(KID_V1, fb);
    return fb;
  }
  throw new Error('Location DEK not installed');
}

async function readKeys(blobKid: string | null): Promise<CryptoKey[]> {
  const out: CryptoKey[] = [];
  const add = (k: CryptoKey | null | undefined) => {
    if (!k) return;
    if (out.includes(k)) return;
    out.push(k);
  };
  if (blobKid) add(keysByKid.get(blobKid));
  add(currentKey);
  add(previousKey);
  keysByKid.forEach((k) => add(k));
  if (!currentKey && !previousKey) {
    add(await devFallbackKey());
  }
  return out;
}

export function isSealedLocation(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const m = raw as Record<string, unknown>;
  return m.v === VERSION && typeof m.iv === 'string' && typeof m.c === 'string' && m.lat == null && m.lng == null;
}

export type LatLng = { lat: number; lng: number };

/** Open sealed or legacy plaintext coordinates. */
export async function openLatLng(raw: unknown): Promise<LatLng | null> {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;

  const plainLat = m.lat ?? m.latitude;
  const plainLng = m.lng ?? m.longitude;
  if (typeof plainLat === 'number' && typeof plainLng === 'number' && Number.isFinite(plainLat) && Number.isFinite(plainLng)) {
    return { lat: plainLat, lng: plainLng };
  }

  if (m.v !== VERSION || typeof m.iv !== 'string' || typeof m.c !== 'string') return null;
  try {
    const iv = base64ToBytes(m.iv);
    const sealed = base64ToBytes(m.c);
    const blobKid = typeof m.kid === 'string' ? m.kid : null;
    const keys = await readKeys(blobKid);
    for (const key of keys) {
      try {
        const clear = await webCrypto().subtle.decrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, sealed);
        const text = new TextDecoder().decode(clear);
        const parts = text.split(',');
        if (parts.length !== 2) continue;
        const lat = Number(parts[0]);
        const lng = Number(parts[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        return { lat, lng };
      } catch {
        /* wrong key — try the next one in the ring */
      }
    }
    return null;
  } catch {
    return null;
  }
}

export async function sealLatLng(lat: number, lng: number): Promise<Record<string, unknown> | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const plain = new TextEncoder().encode(`${lat.toFixed(6)},${lng.toFixed(6)}`);
  const iv = webCrypto().getRandomValues(new Uint8Array(12));
  const key = await getWriteKey();
  const sealed = new Uint8Array(
    await webCrypto().subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, key, plain),
  );
  return { v: VERSION, iv: bytesToBase64(iv), c: bytesToBase64(sealed), kid: currentKid };
}

/** Hydrate liveLocation / routePoints / children stops for map rendering. */
export async function materializeBusDoc(
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const out: Record<string, unknown> = { ...data };

  if (out.liveLocation && typeof out.liveLocation === 'object') {
    const live = { ...(out.liveLocation as Record<string, unknown>) };
    const ll = await openLatLng(live);
    if (ll) {
      live.lat = ll.lat;
      live.lng = ll.lng;
    }
    out.liveLocation = live;
  }

  if (Array.isArray(out.routePoints)) {
    out.routePoints = await Promise.all(
      out.routePoints.map(async (p) => {
        if (!p || typeof p !== 'object') return p;
        const pt = { ...(p as Record<string, unknown>) };
        const ll = await openLatLng(pt);
        if (ll) {
          pt.lat = ll.lat;
          pt.lng = ll.lng;
        }
        return pt;
      }),
    );
  }

  if (Array.isArray(out.children)) {
    out.children = await Promise.all(
      out.children.map(async (c) => {
        if (!c || typeof c !== 'object') return c;
        const child = { ...(c as Record<string, unknown>) };
        const stop = await openLatLng(child.stopLocation);
        if (stop) child.stopLocation = { lat: stop.lat, lng: stop.lng };
        if (Array.isArray(child.safeDropOffStops)) {
          child.safeDropOffStops = await Promise.all(
            child.safeDropOffStops.map(async (e) => {
              if (!e || typeof e !== 'object') return e;
              const row = { ...(e as Record<string, unknown>) };
              const ll = await openLatLng(e);
              if (ll) {
                row.lat = ll.lat;
                row.lng = ll.lng;
              }
              return row;
            }),
          );
        }
        return child;
      }),
    );
  }

  return out;
}
